import { useOrganization, useOrganizationList } from "@clerk/react";
import { useNavigate } from "@tanstack/react-router";
import { Check, ChevronsUpDown } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "—"
  );
}

/**
 * Custom org switcher (ENH-004). Lists every provider this identity belongs to
 * and switches the active Clerk organisation; on switch we invalidate all tRPC
 * caches (so auth.me + data re-resolve to the new tenant) and route to `/` to
 * re-pick provider vs portal. A single-org identity shows just the brand label.
 */
export function OrgSwitcher({
  variant = "sidebar",
}: {
  variant?: "sidebar" | "header";
}): JSX.Element {
  const orgsQuery = trpc.auth.myOrganizations.useQuery();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList();
  const utils = trpc.useUtils();
  const navigate = useNavigate();

  const orgs = orgsQuery.data ?? [];
  const activeOrgId = organization?.id ?? null;
  const current =
    orgs.find((o) => o.clerkOrgId === activeOrgId) ?? orgs[0] ?? null;
  const name = current?.displayName ?? "AMSaaS Portal";
  const multiple = orgs.length > 1;

  async function switchTo(clerkOrgId: string): Promise<void> {
    if (!setActive || clerkOrgId === activeOrgId) return;
    await setActive({ organization: clerkOrgId });
    await utils.invalidate();
    void navigate({ to: "/" });
  }

  const triggerClasses =
    variant === "sidebar"
      ? "flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left hover:bg-sidebar-accent"
      : "flex items-center gap-2.5 rounded-md py-1 pr-1 hover:bg-accent";

  const Label = (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-xs font-bold text-primary-foreground">
        {initials(name)}
      </span>
      <span
        className={cn(
          "truncate text-sm font-semibold",
          variant === "sidebar" && "group-data-[collapsible=icon]:hidden",
        )}
      >
        {name}
      </span>
    </>
  );

  // Single org (or still loading) — non-interactive brand label.
  if (!multiple) {
    return (
      <div
        className={cn(
          "flex items-center gap-2",
          variant === "sidebar" ? "px-1 py-1.5" : "gap-2.5",
        )}
      >
        {Label}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={triggerClasses}>
        {Label}
        <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-60">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Switch provider
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {orgs.map((o) => (
          <DropdownMenuItem
            key={o.tenantId}
            onClick={() => void switchTo(o.clerkOrgId)}
          >
            <Avatar className="size-6 rounded-md">
              <AvatarFallback className="rounded-md text-[10px]">
                {initials(o.displayName)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{o.displayName}</span>
            {o.clerkOrgId === activeOrgId && (
              <Check className="ml-auto size-4" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
